import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { rootRoute } from 'routes/routes';
import { getAppDisplayName } from 'utils/schemas/companySchema';
import { Button } from '@/components/ui/button';

type AppNotAvailableProps = Readonly<{
  clientId: string;
}>;

/**
 * Sidebar-facing product labels, so this page names the product exactly the way the nav does.
 * The English fallbacks intentionally repeat the sidebar's rather than reusing
 * `getAppDisplayName`, whose internal names ('Hero', 'Journeys') customers do not recognise --
 * a missing locale entry must still render something a customer understands.
 */
const APP_LABELS: Readonly<Record<string, Readonly<{ key: string; label: string }>>> = {
  hero: { key: 'nav-canvassing-app', label: 'Canvassing App' },
  polaris: { key: 'nav-online-estimator', label: 'Online Estimator' },
  presentations: { key: 'nav-presentations', label: 'Presentations' },
};

/**
 * Shown by RequireApp in place of a subtree the signed-in user cannot reach, and *only* when their
 * own company already owns the app -- someone whose admin has not handed them a seat yet. That is
 * the one rejection worth explaining: the fix is a click away on a screen they can already see, and
 * naming the product discloses nothing they do not already know their company bought. Every other
 * rejection renders the generic 404 instead, so an unentitled visitor cannot tell a withheld route
 * from one that never existed (ENG-1169).
 *
 * It renders inside the sidebar chrome rather than redirecting: a bookmarked link should explain why
 * it stopped working instead of silently teleporting.
 *
 * The escape button deliberately does not name where it goes -- "Go to My Presentations" told the
 * reader which apps this account holds. "/" resolves through DefaultLandingRedirect, so it still
 * lands a presentations-only user on decks rather than an empty Leads table.
 */
const AppNotAvailable = ({ clientId }: AppNotAvailableProps): JSX.Element => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const known = APP_LABELS[clientId];
  const appName = known ? t(known.key, known.label) : getAppDisplayName(clientId);

  return (
    <div className="flex flex-1 items-center justify-center p-6">
      <div className="flex w-full max-w-md flex-col items-center gap-6 text-center">
        <div className="rounded-full bg-muted p-6">
          <Lock className="h-10 w-10 text-muted-foreground" />
        </div>

        <div className="space-y-2">
          <h1 className="font-serif text-2xl leading-8">
            {t('app-not-available-title', '{{app}} is not available', { app: appName })}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(
              'app-not-available-no-seat',
              "You don't have access to {{app}} yet. Ask an admin at your company to turn it on for you.",
              { app: appName }
            )}
          </p>
        </div>

        <div className="flex w-full max-w-xs flex-col gap-2">
          <Button onClick={() => navigate(rootRoute)}>{t('not-found-go-home', 'Go Home')}</Button>
        </div>
      </div>
    </div>
  );
};

export default AppNotAvailable;
